// src/context/SiteContentContext.js
// ──────────────────────────────────────────────────────────────────────────
// Real Mbala Training Institute content. Fetches /content if available,
// otherwise uses these values. Edit copy here in one place.
// ──────────────────────────────────────────────────────────────────────────

import React, { createContext, useContext, useEffect, useState } from 'react';
import { fetchSiteContent } from '../lib/cms';

const SiteContentContext = createContext(null);

const FALLBACK_CONTENT = {
  hero: {
    eyebrow: 'Mbala Training Institute',
    motto: 'Making your dreams come true',
    tagline: 'Enhancing and equipping the nation with skills for the future.',
  },

  intro: {
    purpose:
      'Mbala Training Institute is a community skills hub. We empower youths, women and the ' +
      'general public with hands-on, life-purpose skills — so people can earn a living with ' +
      'dignity and build enterprises that fight poverty.',
    mission:
      'To develop impeccable institutional capacity in skills enhancement, innovation, rural ' +
      'enterprise development and research.',
    vision:
      'To become a leading institute in skills training, business innovation and enterprise development.',
    stats: [
      { value: '80%', label: 'Practical training' },
      { value: '4',   label: 'Departments' },
      { value: '13',  label: 'Skills courses' },
      { value: '100%', label: 'Hands-on' },
    ],
  },

  admissions: {
    title: 'Join MTI',
    body:
      'Training is open to youths, women and the general public — no matter where you are starting from. ' +
      'Selection includes a short aptitude chat and counselling so we place you on the right course.',
    deadline: 'Intakes run throughout the year — apply any time',
  },

  news: {
    title: 'News & Events',
    subtitle: 'Graduations, community projects and upcoming intakes.',
  },

  contact: {
    email: 'mbalainstitute@gmail.com',
    phone: '',
    address: 'Plot 1454, President Avenue, Ronald Penza, Mbala (1.8 km from town)',
    postal: 'P.O. Box 420100',
  },
};

export const SiteContentProvider = ({ children }) => {
  const [content, setContent] = useState(FALLBACK_CONTENT);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Pull editable content from Supabase and overlay it on the defaults,
    // section by section, so admin edits win but anything not yet edited
    // falls back to the built-in MTI copy.
    fetchSiteContent()
      .then((overrides) => {
        if (overrides && Object.keys(overrides).length) {
          setContent((base) => {
            const merged = { ...base };
            Object.keys(overrides).forEach((k) => {
              merged[k] = (overrides[k] && typeof overrides[k] === 'object' && !Array.isArray(overrides[k]))
                ? { ...base[k], ...overrides[k] }
                : overrides[k];
            });
            return merged;
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <SiteContentContext.Provider value={{ content, loading }}>
      {children}
    </SiteContentContext.Provider>
  );
};

export const useSiteContent = (section) => {
  const ctx = useContext(SiteContentContext);
  if (!ctx) throw new Error('useSiteContent must be used inside SiteContentProvider');
  if (section) return ctx.content?.[section] || null;
  return ctx;
};
